import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import MoreToolsUserCard from "./MoreToolsUserCard";
import MoreToolsWalletCard from "./MoreToolsWalletCard";
import MoreToolsQuickNavGrid from "./MoreToolsQuickNavGrid";
import MoreToolsSettingsRow from "./MoreToolsSettingsRow";

/**
 * MoreToolsMobileDrawer — the full-screen menu portalled to document.body on
 * phone widths (< 640px): header, user card, optional wallet card, shortcut
 * grid and the settings/language row.
 */
const MoreToolsMobileDrawer = ({
  isDark,
  user,
  fullName,
  role,
  initial,
  onClose,
  onLogout,
  hasWallet,
  walletBalance,
  currency,
  currencyIconUrl,
  quickNavItems,
  onSettings,
  onChangeLanguage,
}) => {
  const { t } = useTranslation();

  return createPortal(
    <div
      className={`sm:hidden fixed inset-0 z-[99999] flex flex-col justify-between ${
        isDark ? "bg-chrome text-surface" : "bg-surface text-heading"
      } animate-in fade-in duration-200`}
    >
      {/* Top Bar Header */}
      <div
        className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${
          isDark ? "border-white/[0.08]" : "border-border-light"
        }`}
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-base">
            {t("more_tools") || "القائمة"}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors border-0 cursor-pointer ${
            isDark
              ? "bg-white/[0.08] hover:bg-white/[0.15] text-white"
              : "bg-surface-muted hover:bg-gray-200 text-heading"
          }`}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} className="text-base" />
        </button>
      </div>

      {/* Scrollable Content Body */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
        <MoreToolsUserCard
          isDark={isDark}
          user={user}
          fullName={fullName}
          role={role}
          initial={initial}
          onNavigate={onClose}
          onLogout={onLogout}
        />

        {hasWallet && (
          <MoreToolsWalletCard
            isDark={isDark}
            balance={walletBalance}
            currency={currency}
            currencyIconUrl={currencyIconUrl}
            onNavigate={onClose}
          />
        )}

        <MoreToolsQuickNavGrid
          isDark={isDark}
          items={quickNavItems}
          onNavigate={onClose}
        />

        <MoreToolsSettingsRow
          isDark={isDark}
          onSettings={onSettings}
          onChangeLanguage={onChangeLanguage}
        />
      </div>
    </div>,
    document.body
  );
};

export default MoreToolsMobileDrawer;
